import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api, requestBlob } from "@/lib/api";
import {
  endpoints,
  type CreditNoteDetail,
  type DocumentEnvelope,
  type PreviewEnvelope,
} from "@/lib/endpoints";
import { creditNoteKeys } from "@/features/credit-notes/use-credit-note-list";

/**
 * One credit note, and the two acts that exist for it (§17.3, s.34(1) CGST).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THERE ARE ONLY TWO WRITES, AND EVERY ABSENCE BELOW IS A RULE.
 *
 *   ISSUE   `POST /credit-notes` — the whole feature.
 *   CANCEL  `POST /credit-notes/:id/cancel` — a status, and the number stays.
 *
 * NO EDIT HOOK. `creditnote.edit` does not exist as a capability for anyone
 * (§2.1), the same as `invoice.edit`, and the API mounts no `PUT`. A wrong
 * credit note is cancelled and a fresh one raised — which is exactly what §34
 * expects, since the wrong one has already been declared in a return.
 *
 * NO DELETE HOOK. §34(1) requires a consecutive series and §5 allows no gaps, so
 * a missing `CRN/26-27/2` is a question from the department.
 *
 * NO PREVIEW HOOK. The other three editors preview because their figures are
 * being composed from scratch; a credit note's lines start as a subset of a bill
 * already on screen, and the API mounts no preview route to call. The editor
 * therefore shows the ORIGINAL's own figures and says the note's are confirmed
 * on save — see `CreditNoteEditorScreen`.
 *
 * NO DRAFT HOOK. A credit note is short and is typed and issued in one sitting;
 * `DraftKind` is a database enum, so adding one later is a deliberate
 * `ALTER TABLE` rather than a state that exists because it was cheap.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/**
 * `GET /credit-notes/:id` → the note, lines and all.
 *
 * A 404 HERE IS AMBIGUOUS BY DESIGN and the caller must not resolve it into
 * words it cannot justify. Every credit-note route is behind
 * `requireFlag("credit_notes")` (§18.3), which runs before `requireCap` and
 * answers 404 rather than 403 so that "forbidden" cannot leak unreleased work —
 * so a 404 means either "no such note" or "the module is off", and
 * `ApiError.isNotFound` deliberately cannot tell them apart. THE FLAG SHIPS
 * DISABLED, so the second is the ordinary case today.
 */
export function useCreditNote(id: string | undefined): UseQueryResult<CreditNoteDetail> {
  return useQuery({
    queryKey: creditNoteKeys.detail(id ?? ""),
    queryFn: async () => {
      const response = await api.get<DocumentEnvelope<CreditNoteDetail>>(
        endpoints.creditNote(id ?? ""),
      );
      return response.data;
    },
    enabled: id !== undefined && id !== "",
    /*
     * FIVE MINUTES — the ISSUED INVOICE's window, not the challan's thirty
     * seconds, and the difference is the document rather than the screen. A
     * credit note is immutable the moment it exists: no line, no total, no date
     * and no rate can ever move. The ONE thing that can change is the status,
     * and cancelling happens on this very screen, which invalidates the entry
     * itself. Nothing else in the system writes to this row.
     */
    staleTime: 300_000,
  });
}

/**
 * The credit note as paper — fetched as BYTES, never navigated to.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * NO `copy` ARGUMENT, AND THAT IS THE DIFFERENCE FROM `useInvoicePdf` AND
 * `useChallanPdf`. Rule 55(2) gives a delivery challan three statutory copies
 * with a caption naming who holds each, and an invoice has a `copy_type` column;
 * `credit_notes` has neither, and `GET /credit-notes/:id/pdf` accepts no `?copy=`.
 * Offering a "Duplicate" that printed the identical sheet would be a menu
 * inventing a distinction the document does not have.
 *
 * WHY NOT `<a href="/api/v1/credit-notes/12/pdf">`. The route is authenticated
 * with a BEARER token (§1.1, so the Capacitor shell works unchanged). A browser
 * navigation sends no Authorization header and renders the API's 401 JSON inside
 * a PDF viewer. The only way to open it is to fetch it with the header and hand
 * the bytes to the viewer as an object URL.
 *
 * EVERY OBJECT URL IS REVOKED — the previous one before a new one is made, and
 * the last one on unmount. `URL.createObjectURL` pins the whole blob until it is
 * revoked or the tab closes.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function useCreditNotePdf(id: string | undefined, number: string | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  const release = () => {
    if (urlRef.current !== null) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  };

  useEffect(() => release, []);

  const mutation = useMutation({
    mutationFn: async () => {
      // `CRN/26-27/1` is not a filename. The number already carries its prefix,
      // so nothing is added — the server names it `CRN_26-27_1_Lupin.pdf`.
      const fallback = `${(number ?? `credit-note-${id ?? ""}`).replace(/\//g, "-")}.pdf`;
      return requestBlob(endpoints.creditNotePdf(id ?? ""), { fallbackFilename: fallback });
    },
    onSuccess: (result) => {
      release();
      const next = URL.createObjectURL(result.blob);
      urlRef.current = next;
      setUrl(next);
      setFilename(result.filename);
    },
  });

  return { ...mutation, url, filename, release };
}

/**
 * `POST /credit-notes` — issue one. Consumes a number, and cannot be undone (§6).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHAT THIS SENDS, AND WHAT IT DELIBERATELY DOES NOT.
 *
 * It sends `{ invoiceId, date, reason, attnName?, notes?, lines }` and nothing
 * else. `CreditNoteCreateSchema` is a plain `z.object()`, so unknown keys are
 * STRIPPED rather than refused — which means a hopeful `customerId` or
 * `supplyType` would not error, it would simply be ignored for ever. Every one
 * of those is the server's (§6): the number comes from the company-row lock, the
 * buyer and the supply type are COPIED off the locked invoice, the rates resolve
 * as of the ORIGINAL invoice's date (§8.7) and every total is recomputed.
 *
 * `warnings` IS NOT AN ERROR. §34(2)'s deadline — 30 November following the end
 * of the financial year of the supply — arrives as a sentence here, on a
 * response that also created the document. The note IS issued; what may be lost
 * is the tax adjustment. Show it; never swallow it, and never paint it red.
 *
 * IT INVALIDATES EVERYTHING, and that is not laziness. An issued credit note
 * changes what the customer owes, so it moves the bill's payment card, the
 * unpaid register, the per-customer statement and the home-screen "who owes me
 * money" figure — none of which is under the `creditNotes` prefix, and all of
 * which are folds over one server producer. A narrower invalidation leaves the
 * owner looking at an outstanding total that still includes money he has just
 * credited back, which is the single most damaging figure this app can get
 * wrong: he reads it out to the customer.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function useIssueCreditNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api.post<PreviewEnvelope<CreditNoteDetail>>(endpoints.creditNotes, body),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

/**
 * `POST /credit-notes/:id/cancel { reason }`.
 *
 * A STATUS, NEVER A DELETE, AND THE NUMBER STAYS CONSUMED — §34(1) requires a
 * consecutive series, so a missing number is a question from the department.
 *
 * IT PUTS THE MONEY BACK, AND THAT IS THE POINT. Every sum over `credit_notes`
 * counts ISSUED rows only, so this one status change returns the credited amount
 * to what the customer owes, on the bill and the unpaid register and the
 * statement and the home screen at once. There is deliberately no compensating
 * entry: a cancelled credit note credits nothing, which is what cancelling
 * means. The caller must invalidate widely for exactly that reason.
 *
 * THE REASON IS MANDATORY, for the reason it is on every other cancellation
 * here: the whole justification for cancelling rather than deleting is that the
 * record survives AND EXPLAINS ITSELF, and `audit_log` is an ADMIN surface
 * (`audit.view`, §2.1) the owner cannot open.
 *
 * `CreditNoteCancelSchema` IS `.min(1)`, NOT `.min(3)`. The dialog guards with
 * three characters like the invoice cancel and the quotation void, which is safe
 * because it is STRICTER than the API — but do not write a comment claiming the
 * server enforces three. `maxLength={500}` is real on all of them.
 */
export function useCancelCreditNote(id: string | undefined) {
  return useMutation({
    mutationFn: async (reason: string) => {
      const response = await api.post<DocumentEnvelope<CreditNoteDetail>>(
        endpoints.creditNoteCancel(id ?? ""),
        { reason },
      );
      return response.data;
    },
  });
}
